/**
 * Presence-store — één gedeelde eigen-presence-status voor alle tabs.
 *
 * Vóór deze store haalde elke PresenceToggle zijn status één keer op bij
 * mount: zet je jezelf op afwezig op scherm 1, dan bleef scherm 2 "Beschikbaar"
 * tonen tot een herlaad. Nu leeft de status module-scoped, synct hij over
 * tabs via de phone-bus (BroadcastChannel echoot niet naar de afzender, dus
 * de afzender past zijn eigen store direct aan) en refetcht elke tab bij
 * zichtbaar worden (vangt ook wijzigingen van een ander apparaat).
 *
 * NB: dit is de HANDMATIGE presence (available/afwezig). De automatische
 * in-gesprek-status (busy_since) loopt apart via client.ts → syncBusyState.
 */

import { supabase } from '../supabase';
import * as phoneBus from './phone-bus';

export type Presence = 'available' | 'dnd';

export interface PresenceState {
  presence: Presence;
  provisioned: boolean;
  /** Eigen ring_enabled uit telephony_users: false = geen ring-target, de
   *  routering dialt deze gebruiker nooit (Realtime-ring-seed moet dan zwijgen,
   *  anders toont de UI een onbeantwoordbaar spook-belletje — incident 2026-07-07). */
  ringEnabled: boolean;
  /** Server-capability doorverbinden/wachtstand (TELEPHONY_TRANSFER_ENABLED);
   *  false = knoppen verbergen (rollback-pad). */
  transferEnabled: boolean;
  loaded: boolean;
  saving: boolean;
}

let state: PresenceState = {
  presence: 'available',
  provisioned: false,
  ringEnabled: true,
  transferEnabled: false,
  loaded: false,
  saving: false,
};

type Listener = () => void;
const listeners = new Set<Listener>();
let wired = false;

function setState(patch: Partial<PresenceState>): void {
  state = { ...state, ...patch };
  listeners.forEach((l) => {
    try {
      l();
    } catch (err) {
      console.error('[telephony/presence-store] listener error', err);
    }
  });
}

export function getPresenceState(): PresenceState {
  return state;
}

export function subscribePresence(listener: Listener): () => void {
  initPresenceStore();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

async function refetch(): Promise<void> {
  try {
    const r = await fetch('/api/telephony/presence', { headers: await authHeaders() });
    if (!r.ok) return;
    const json = (await r.json()) as {
      presence?: string;
      provisioned?: boolean;
      ringEnabled?: boolean;
      transferEnabled?: boolean;
    };
    setState({
      presence: json.presence === 'dnd' ? 'dnd' : 'available',
      provisioned: Boolean(json.provisioned),
      ringEnabled: json.ringEnabled !== false,
      transferEnabled: Boolean(json.transferEnabled),
      loaded: true,
    });
  } catch {
    // stil falen — presence is geen kritisch UI-element
  }
}

/** Idempotent: eerste subscriber wire't fetch + bus + focus-refetch. */
export function initPresenceStore(): void {
  if (wired) return;
  wired = true;

  void refetch();

  // Andere tab wijzigde de presence → direct overnemen (geen refetch nodig,
  // de afzender heeft de server al bijgewerkt).
  phoneBus.onMessage((msg) => {
    if (msg.kind !== 'presence') return;
    setState({ presence: msg.presence, loaded: true });
  });

  // Tab wordt zichtbaar → verversen; vangt wijzigingen vanaf een ander
  // apparaat of een verlopen sessie (BroadcastChannel is per browser-profiel).
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && state.loaded) void refetch();
    });
  }
}

/** Optimistic zetten + server-write + broadcast; rollback bij falen. */
export async function setPresence(next: Presence): Promise<void> {
  const prev = state.presence;
  if (prev === next) return;
  setState({ presence: next, saving: true });
  phoneBus.postPresence(next);
  try {
    const r = await fetch('/api/telephony/presence', {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ presence: next }),
    });
    if (!r.ok) throw new Error(`presence save failed: ${r.status}`);
    setState({ saving: false });
  } catch (err) {
    console.warn('[telephony/presence-store] save failed', err);
    setState({ presence: prev, saving: false });
    phoneBus.postPresence(prev);
  }
}
